"use client";

import { Activity, ArrowRight, Compass, Layers, Target, TrendingUp, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Growthstrategyconsultation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: visualVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const statChips = [{
    icon: Target,
    title: "1 unified growth plan",
    description: "A single strategic roadmap aligning SEO, GEO, content, and conversions."
  }, {
    icon: Layers,
    title: "6 integrated systems",
    description: "Interconnected AI search, web, CRM, review, and automation engines."
  }, {
    icon: TrendingUp,
    title: "90-day milestones",
    description: "Structured sprint cycles focused on high-impact revenue levers."
  }, {
    icon: Activity,
    title: "Data-driven decisions",
    description: "Transparent real-time tracking eliminating marketing guesswork."
  }];
  const metrics = [{
    label: "AI & Search Domination",
    sub: "+48% visibility index",
    progress: "w-[94%]"
  }, {
    label: "Lead Conversion Velocity",
    sub: "+142% booked jobs",
    progress: "w-[88%]"
  }, {
    label: "Automated Pipeline Synergy",
    sub: "Zero lost inquiries",
    progress: "w-[100%]"
  }];
  return <section data-section-id="818" id="growthstrategyconsultation" className="relative min-h-[88vh] flex flex-col justify-between bg-background text-foreground py-16 sm:py-20 lg:py-24 overflow-hidden">
      {/* Radial dot-grid background */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "radial-gradient(circle, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      {/* Subtle deep red ambient glow */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-lg opacity-25 blur-3xl" style={{
      background: "radial-gradient(circle, var(--accent-deep-red) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto w-full max-w-7xl px-6 lg:px-8 flex-1 flex flex-col justify-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center my-auto">
          {/* Left Column: Eyebrow, Headline, Subtext, Dual CTAs */}
          <div ref={headerRef} className={`lg:col-span-7 transition-all duration-700 ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <div className="inline-flex items-center gap-2 rounded-lg border border-[var(--accent-deep-red)]/40 bg-card/50 px-4 py-1.5 backdrop-blur-sm shadow-sm">
              <Compass className="h-4 w-4 text-[var(--accent-deep-red)]" />
              <span className="font-mono text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                GROWTH STRATEGY &amp; CONSULTING
              </span>
            </div>

            <h1 className="font-default mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.08]">
              Sustainable growth takes{" "}
              <br className="hidden sm:inline" />
              <span className="italic font-serif font-normal bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(135deg, var(--accent-deep-red) 0%, hsl(352 54% 70%) 50%, #ffffff 100%)"
            }}>
                more than marketing.
              </span>
            </h1>

            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl">
              ClearLine builds unified growth plans engineered for local service authority. We combine AI search optimization (GEO), SEO, automated lead intake, custom web infrastructure, and data-driven decision making to scale your revenue with absolute precision.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild className="bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]/90 h-12 px-7 rounded-lg font-medium shadow-md transition-all">
                <Link className="inline-flex items-center gap-2" to="/PricingPlansPage"><span>Get started now</span><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right h-4 w-4" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
              </Button>
              <Button asChild variant="outline" className="border-border hover:bg-accent/50 h-12 px-7 rounded-lg font-medium transition-all">
                <Link to="/VisibilityCheckupPage">Free AI Visibility Checkup</Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Growth Strategy Mockup Card */}
          <div ref={visualRef} className={`lg:col-span-5 transition-all duration-1000 ${visualVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <div className="relative rounded-2xl border border-border bg-card/80 p-6 sm:p-8 backdrop-blur-md shadow-xl overflow-hidden">
              {/* Card ambient glow */}
              <div aria-hidden="true" className="pointer-events-none absolute -right-20 -bottom-20 h-64 w-64 rounded-lg opacity-20 blur-3xl" style={{
              background: "radial-gradient(circle, var(--accent-deep-red) 0%, transparent 70%)"
            }} />

              {/* Card Header */}
              <div className="flex items-center justify-between pb-6 border-b border-border/60">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--accent-deep-red)]/15 text-[var(--accent-deep-red)] border border-[var(--accent-deep-red)]/30">
                    <Zap className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">
                      Strategy Execution Vector
                    </h2>
                    <p className="text-xs font-mono text-muted-foreground">
                      6-System Integrated Ecosystem
                    </p>
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background/60 px-2.5 py-1 text-xs font-mono text-muted-foreground">
                  <span className="h-2 w-2 rounded-lg bg-emerald-500 animate-pulse" />
                  Live Strategy
                </div>
              </div>

              {/* SVG Rising Path Graphic */}
              <div className="mt-6">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Compounding Growth Trajectory
                  </span>
                  <span className="text-xs font-bold font-mono text-[var(--accent-deep-red)]">
                    90-Day Sprint Target: +140%
                  </span>
                </div>

                <div className="relative h-36 w-full">
                  <svg className="h-full w-full overflow-visible" viewBox="0 0 400 130" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="growthChartGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--accent-deep-red)" stopOpacity="0.45" />
                        <stop offset="100%" stopColor="var(--accent-deep-red)" stopOpacity="0" />
                      </linearGradient>
                    </defs>

                    <path d="M 0,115 Q 80,100 150,75 T 280,35 T 400,10 L 400,130 L 0,130 Z" fill="url(#growthChartGrad)" />
                    <path d="M 0,115 Q 80,100 150,75 T 280,35 T 400,10" fill="none" stroke="var(--accent-deep-red)" strokeWidth="3" strokeLinecap="round" />

                    <circle cx="150" cy="75" r="4" fill="var(--accent-deep-red)" />
                    <circle cx="280" cy="35" r="4" fill="var(--accent-deep-red)" />
                    <circle cx="400" cy="10" r="5" fill="var(--accent-deep-red)" className="animate-ping" />
                    <circle cx="400" cy="10" r="5" fill="var(--accent-deep-red)" />
                  </svg>
                </div>
              </div>

              {/* KPI Rows with progress bars */}
              <div className="mt-6 space-y-3.5 pt-5 border-t border-border/60">
                {metrics.map((m, i) => <div key={i} data-index={i} className="space-y-1">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-medium text-foreground">{m.label}</span>
                      <span className="font-mono text-muted-foreground">{m.sub}</span>
                    </div>
                    <div className="h-2 w-full rounded-lg bg-muted/40 overflow-hidden relative">
                      <div className={`h-full rounded-lg bg-[var(--accent-deep-red)] transition-all duration-1000 ${m.progress}`} />
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Horizontal Stat Chips */}
        <div ref={statsRef} className={`mt-16 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 transition-all duration-1000 ${statsVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          {statChips.map((chip, i) => {
          const ChipIcon = chip.icon;
          return <div key={i} data-index={i} className="group flex items-center gap-4 rounded-xl border border-border bg-card/80 p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[var(--accent-deep-red)]/40 hover:shadow-md">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] group-hover:bg-[var(--accent-deep-red)]/20 transition-colors">
                  <ChipIcon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {chip.title}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-normal">
                    {chip.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}